import { gql } from "lib/apolloClient";
import {
  staffFragment,
  studentFragment,
  attachmentFragment,
  academicYearFragment,
} from "modules/CommonFragments";
import { dpExamRegFragment } from "DPExamRegistration/modules/DPExamRegistrationFragments";
import {
  commonFragments,
  studentProjectFragment,
  studentInfofragment,
  continuumBasicDetailsFragments,
  studentOffenceFragments,
  gainContinuumFragment,
  IBPYPElementRatingFragments,
  studentAlertFragment,
} from "./ProfileDetailsFragments";

export const getOrganizationConstantsQuery = gql`
  query getOrganizationConstants(
    $organizationId: ID!
    $filter: GlobalConstantsFilterOptions
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        globalConstants(filter: $filter) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getStudentBehaviourSummaryQuery = gql`
  query getStudentBehaviourSummary($id: ID!, $input: BehaviourSummaryInput) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        behaviourSummary(input: $input) {
          edges {
            node {
              id
              summary
              createdAt
              updatedAt
            }
          }
        }
      }
    }
  }
`;

export const getStudentProjectGroupIdsQuery = gql`
  query getStudentProjectGroupIds(
    $id: ID!
    $projectGroupFilters: UserProjectGroupFilter
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        projectGroups(filters: $projectGroupFilters) {
          id
          type
        }
      }
    }
  }
`;

export const studentProjectQuery = gql`
  query getStudentProjectQuery(
    $id: ID!
    $filters: ProjectGroupProjectsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        type
        isInvited
        deadlines {
          id
          deadlineDate
          title
        }
        projects(filters: $filters) {
          edges {
            node {
              ...projectInfo
            }
          }
        }
      }
    }
  }
  ${studentProjectFragment.projectFragment}
`;

export const studentServiceAsAction = gql`
  query getStudentServiceAsAction(
    $id: ID!
    $filters: ProjectGroupProjectsFilters
  ) {
    node(id: $id, type: PROJECT_GROUP) {
      ... on ProjectGroup {
        id
        projects(filters: $filters) {
          edges {
            node {
              id
              status
              reportStatus
              supervisors {
                ...staffItem
              }
              students {
                ...studentItem
              }
              fields {
                ...plannerFieldItem
              }
            }
          }
        }
      }
    }
  }
  ${staffFragment.basicStaffDetails}
  ${studentFragment.basicStudentDetails}
  ${commonFragments.plannerField}
`;

export const examRegistrationStudent = gql`
  query getExamRegistrationStudent($id: ID!) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        id
        yearGroup {
          id
          isArchived
          examRegistration {
            id
            studentShowExamDetailsPerm
            canStudentEditCandidateInfo
            canStudentEditDpSubjects
            examSessions {
              id
              session
              month
              year
            }
            student: studentByUserId(id: $id) {
              ...ibisUserBasicDetails
              ibisSubjects {
                ...ibisUserSubjects
              }
            }
            yearGroup {
              id
              isArchived
              examSession {
                id
                session
              }
            }
          }
        }
      }
    }
  }
  ${dpExamRegFragment.ibisUserBasicDetailsFragment}
  ${dpExamRegFragment.ibisUserSubjectFragment}
`;

export const getStudentAdditionalInfoQuery = gql`
  query getStudentAdditionalInfo($studentId: ID!) {
    node(id: $studentId, type: STUDENT) {
      ...studentAdditionalInfoItem
    }
  }
  ${studentInfofragment.additionalInfo}
`;

export const studentFilesQuery = gql`
  query getStudentFiles(
    $id: ID!
    $searchText: String
    $types: [ORGANIZATION_RESOURCE_TYPE_ENUM!]
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $after: String
    $first: Int
    $states: [ORGANIZATION_RESOURCE_STATE_ENUM]
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        firstName
        lastName
        studentFiles(
          filters: { types: $types, searchText: $searchText, states: $states }
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          first: $first
          after: $after
        ) {
          resourceTypeBasedCount {
            fileCount
            folderCount
            totalCount
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          edges {
            node {
              id
              label
              type
              resourceType
              attachment {
                ...attachmentItem
              }
              createdAt
            }
            cursor
          }
        }
      }
    }
  }
  ${attachmentFragment.attachment}
`;

export const continuumBasicDetailsQuery = gql`
  query getOrganizationContinuumFeed($id: ID!, $filters: ContinuumFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        continuums(filters: $filters) {
          totalCount
          edges {
            node {
              ...continuumBasicDetails
            }
          }
        }
      }
    }
  }
  ${continuumBasicDetailsFragments.continuumBasicDetails}
`;

export const studentOffenceListQuery = gql`
  query getStudentOffenceList($id: ID!, $filters: StudentOffenseFilter) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        studentOffense(filters: $filters) {
          edges {
            node {
              ...studentOffence
            }
          }
        }
      }
    }
  }
  ${studentOffenceFragments.studentOffence}
`;

export const getGainContinuumQuery = gql`
  query getGainContinuum($id: ID!, $filters: ContinuumRatingsFilter) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        continuumRatings(filters: $filters) {
          ...continuumBasicDetailsItem
        }
      }
    }
  }
  ${gainContinuumFragment.continuumBasicDetails}
`;

export const ibPYPElementRatingsGenericQuery = gql`
  query ibPYPElementRatingsGeneric(
    $id: ID!
    $dataSet: IB_PYP_RATING_DATA_SET_ENUM!
    $filters: ibPYPElementRatingFiltersForGeneric!
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        ibPYPElementRatings(dataSet: $dataSet, filtersForGeneric: $filters) {
          ...IBPYPElementRatingBasicDetailsItem
        }
      }
    }
  }
  ${IBPYPElementRatingFragments.IBPYPElementRatingBasicDetails}
`;

export const getStudentCoursesQuery = gql`
  query getStudentCourses(
    $studentId: ID!
    $courseFilters: UserCourseMapFilters
  ) {
    node(id: $studentId, type: STUDENT) {
      ...studentCoursesItem
    }
  }
  ${studentInfofragment.studentCourses}
`;

export const getGainContinuumRemarkQuery = gql`
  query getGainContinuumRemark($id: ID!, $filters: ContinuumRemarksFilter) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        continuumRemarks(filters: $filters) {
          ...continuumRemarkBasicDetailsItem
        }
      }
    }
  }
  ${gainContinuumFragment.continuumRemarkBasicDetails}
`;

export const getStudentPortfolioDownloadInfoQuery = gql`
  query getStudentPortfolioDownloadInfo($id: ID!, $filters: PostFilterInput) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        posts(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getStudentBillingInfoQuery = gql`
  query getStudentBillingInfo(
    $studentId: ID!
    $input: StudentBillingInfoInput!
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        studentBillingInfo(input: $input)
      }
    }
  }
`;

export const getStudentBillingPaymentLinkQuery = gql`
  query getStudentBillingPaymentLink($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        globalConstants(
          filter: { names: ["PEOPLESOFT_BILLING_PAYMENT_LINK"] }
        ) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getStudentSisServiceIdQuery = gql`
  query getStudentSisServiceId($id: ID!) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        sisServiceId
      }
    }
  }
`;

export const getCentInspireLinkQuery = gql`
  query getCentInspireLink($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        globalConstants(filter: { names: ["CENT_INSPIRE_LINK"] }) {
          id
          name
          value
        }
      }
    }
  }
`;

export const getStudentPortfolioDownloadJobCountForUserQuery = gql`
  query getStudentPortfolioDownloadJobCount(
    $id: ID!
    $filters: OrganizationIntegrationJobFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        jobFeed(filters: $filters) {
          totalCount
        }
      }
    }
  }
`;

export const getStudentAcademicYearsQuery = gql`
  query getStudentAcademicYears(
    $id: ID!
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        isArchived
        isArchivedV2(filters: $userArchivedFilters)
        academicYears {
          ...academicYearItem
        }
      }
    }
  }
  ${academicYearFragment.academicYear}
`;

export const getStudentCurriculumsQuery = gql`
  query getStudentCurriculums(
    $id: ID!
    $filters: StudentCurriculumProgramFilter
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        curriculumPrograms(filters: $filters) {
          id
        }
      }
    }
  }
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: CourseFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        courses(filters: $filters) {
          id
        }
      }
      ... on Student {
        courses(filters: $filters) {
          id
        }
      }
    }
  }
`;

export const getStudentAlertTypesQuery = gql`
  query getStudentAlertTypes($id: ID!, $filters: StudentAlertTypeFilters) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        studentAlertTypes(filters: $filters) {
          id
          name
          icon
          isEnabled
        }
      }
    }
  }
`;

export const getStudentAlertsQuery = gql`
  query getStudentAlerts(
    $studentId: ID!
    $first: Int
    $after: String
    $filters: StudentAlertFilters
  ) {
    node(id: $studentId, type: STUDENT) {
      ... on Student {
        id
        alerts(filters: $filters, first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...studentAlertFields
            }
          }
        }
      }
    }
  }
  ${studentAlertFragment}
`;
